import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, User, ArrowRight, CheckSquare } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const inviteEmail = searchParams.get("email");
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState(inviteEmail || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteValid, setIsInviteValid] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const isEmployeeRegistration = !!inviteToken;

  useEffect(() => {
    if (inviteToken) {
      verifyInvitation();
    }
  }, [inviteToken]);

  const verifyInvitation = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invitations/verify/${inviteToken}`);
      setIsInviteValid(true);
      setEmail(response.data.email);
      setInviteName(response.data.name);
      setInviteRole(response.data.role || "employee");
      setName(response.data.name);
    } catch (error) {
      toast.error("Invalid or expired invitation");
      setIsInviteValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const userData = {
        email,
        password,
        name,
        role: isEmployeeRegistration ? inviteRole : "admin",
        invite_token: inviteToken || undefined,
      };
      
      const user = await register(userData);
      toast.success(`Welcome to Future Owners, ${user.name}!`);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmployeeRegistration && !isInviteValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-b from-white to-secondary/20">
        <Card className="w-full max-w-md border-border/50 shadow-soft">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Invalid Invitation
            </h2>
            <p className="text-muted-foreground mb-6">
              This invitation link is invalid or has expired.
            </p>
            <Link to="/login">
              <Button className="rounded-full" data-testid="back-to-login-button">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-split">
      {/* Left Side - Image */}
      <div 
        className="auth-image relative hidden md:flex items-center justify-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1710020319182-3102611ed86b?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#663399]/80"></div>
        <div className="relative z-10 text-white p-12 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/images/logo.png" alt="Future Owners Logo" className="w-16 h-16 object-contain" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEmployeeRegistration ? "Join as Future Owner" : "Start Managing Responsibilities"}
          </h2>
          <p className="text-lg text-white/80">
            {isEmployeeRegistration 
              ? "You've been invited to join Future Owners. Create your account to start choosing responsibilities."
              : "Create your admin account and start organizing responsibilities for your team."
            }
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex items-center justify-center p-8 bg-gradient-to-b from-white to-secondary/20">
        <Card className="w-full max-w-md border-border/50 shadow-soft" data-testid="register-card">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="md:hidden flex items-center justify-center gap-2 mb-4">
              <img src="/images/logo.png" alt="Future Owners Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
            </div>
            <CardTitle className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isEmployeeRegistration ? "Complete Your Profile" : "Create Account"}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {isEmployeeRegistration 
                ? `Welcome ${inviteName}! You're joining as ${inviteRole === "admin" ? "an Admin" : "a Future Owner"}. Set up your password to get started.`
                : "Sign up as an admin to manage your team's responsibilities"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                    disabled={isEmployeeRegistration}
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                disabled={isLoading}
                data-testid="register-submit-button"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                <span>{isEmployeeRegistration ? "Complete Registration" : "Create Admin Account"}</span>
                    <ArrowRight className="h-5 w-5" />
                  </div>
                )}
              </Button>
            </form>
            {!isEmployeeRegistration && (
              <div className="mt-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-primary font-medium hover:underline"
                    data-testid="login-link"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
